import { useCallback, useState, type FormEvent } from "react";

import { useProject } from "../app/projectContext";
import { useStudyDraft } from "../hooks/useStudyDraft";
import {
  busbarEvidenceRunCreateRequestSchema,
  type BusbarEvidenceRunCreateRequest,
} from "../services/busbarEvidence";
import { busbarKindSchema, busbarMaterialSchema } from "../services/busbarEvidenceContract";
import { voltageDropBasisSchema } from "../services/busbarSelectionContract";
import type { SldElements } from "../services/sldElements";
import { describeValidationIssue, type ValidationIssueLike } from "../utils/validationMessages";
import {
  BUSBAR_EVIDENCE_DRAFT_MODULE,
  BUSBAR_EVIDENCE_LABELS,
  BUSBAR_KIND_LABELS,
  BUSBAR_MATERIAL_LABELS,
  FAULT_LEVEL_SOURCE_LABELS,
  FAULT_LEVEL_SOURCES,
  busbarEvidenceDraftSchema,
  buildBusbarEvidenceRunPayload,
  createInitialBusbarEvidenceDraft,
  type BusbarEvidenceDraft,
  type FaultLevelSource,
} from "./busbarEvidenceDraft";
import { VOLTAGE_DROP_BASIS_LABELS } from "./busbarSelectionDraft";
import { FaultRunSelector } from "./FaultRunSelector";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker, sldElementsOf } from "./SldElementPicker";
import { TYPED_HINT } from "./StarterCoordinationForm";

/** The first option of every choice: nothing is chosen for the engineer (A24 (b)). */
const CHOOSE = "Choose…";

export { TYPED_HINT };

// The words of the fault-run selector live with the shared selector (A27 (e)); re-exported
// here for the pages and tests that read them from this form.
export {
  FAULT_RUN_NOT_LINKABLE,
  NO_FAULT_RUNS_NOTE,
  NO_OPEN_REVISION_NOTE,
  NOT_MAXIMUM_THREE_PHASE_NOTE,
  busMismatchNote,
  faultRunOptionText,
} from "./FaultRunSelector";

export const NO_PEAK_NOTE =
  "This fault run has no peak current; the peak withstand check will be Engineering review " +
  "required.";
export { linkedDetailText, linkedDetailTitle } from "./BreakerSelectivityForm";

export const RATED_CURRENT_HINT =
  "The builder's design-verified rated current (IEC 61439) at the site conditions. The OS " +
  "does not derate.";

export const SITE_AMBIENT_HINT =
  "A site ambient above the builder's reference ambient is flagged for Engineering review: " +
  "derating needs the builder's data.";

export const FAULT_DURATION_HINT =
  "The fault clearing time of the upstream protection, for example the incomer's short-time " +
  "delay.";

export const WITHSTAND_HINT =
  "From the builder's design verification. The engine checks the declared evidence; it does " +
  "not prove the busbar. The PE bar is not evaluated.";

export const NEUTRAL_HINT =
  "Optional. Fill both values to check the neutral bar. A neutral rated below the phase " +
  "busbar needs a harmonic or load assessment: answer Yes only when one is held.";

export const TRUNKING_HINT =
  "From the trunking builder's data. State what the declared drop refers to; without it " +
  "the voltage drop is not calculated and the study needs review.";

export const PROFILE_NOTE =
  "The busbar evidence checks carry no profile-dependent value; the profile is recorded " +
  "with the run and must match the design basis of the project revision.";


type BusbarEvidenceFormProps = {
  disabled?: boolean;
  onSubmit: (payload: BusbarEvidenceRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): the bus is
  // picked from it and its voltage fills the typed system voltage while that is blank.
  sldElements?: SldElements | null | undefined;
};

// The LV busbar evidence study (EOS-07a, A24). The form owns the draft. The fault
// level starts on a linked fault run of the active open revision (A24 (c)); the runs
// are listed from the fault runs API and the chosen run's stored Ik'' and ip are
// shown read-only, for the engineer's eyes only - the backend reads the same stored
// result again when the study is saved. A typed value is allowed and said to be
// review required. Every choice starts unchosen: the backend imposes no default
// (A24 (b)). The trunking fieldset is shown for a busbar trunking study only (A24 (f)).
//
// The draft is kept under its own module key, "busbar-evidence"; no form ever
// reads, restores or deletes another's draft.
//
// The payload carries no project revision: the page adds the selected revision
// when it calls the API, exactly as the other study pages do.
export function BusbarEvidenceForm({
  disabled = false,
  onSubmit,
  sldElements,
}: BusbarEvidenceFormProps) {
  // The entries outlive a reload, Back and the sidebar for as long as the tab is open.
  const { activeRevisionId } = useProject();
  const revisionId = activeRevisionId();
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<BusbarEvidenceDraft>({
      module: BUSBAR_EVIDENCE_DRAFT_MODULE,
      createInitial: createInitialBusbarEvidenceDraft,
      schema: busbarEvidenceDraftSchema,
      revisionId,
    });

  const [validationError, setValidationError] = useState<string | null>(null);

  const linkedSource = draft.faultLevelSource === "LINKED";

  function updateStudy(patch: Partial<BusbarEvidenceDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  // The bus picked from the diagram; its voltage fills the typed system voltage while that is
  // still blank, never over a value the engineer typed (A27 (f)).
  function chooseBus(busCode: string) {
    const bus = sldElementsOf(sldElements, ["bus"]).find((element) => element.code === busCode);
    setDraft((current) => ({
      ...current,
      busCode,
      typedSystemNominalVoltageV:
        current.typedSystemNominalVoltageV.trim() === "" && bus?.voltage_v
          ? bus.voltage_v
          : current.typedSystemNominalVoltageV,
    }));
  }

  // Stable for the selector's effects: it clears a run the listed revision does not hold.
  const setFaultRunId = useCallback(
    (faultRunId: string) => setDraft((current) => ({ ...current, faultRunId })),
    [setDraft],
  );

  // Empties the form only; a result already on the page stays until "Clear results".
  function clearForm() {
    setValidationError(null);
    clear();
  }

  // The selector's own sentence stands alone; every other issue is read by its label.
  function describeIssue(issue: ValidationIssueLike): string {
    if (issue.code === "custom" && issue.path.at(-1) === "fault_run_id") {
      return issue.message;
    }
    return describeValidationIssue(issue, BUSBAR_EVIDENCE_LABELS);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = busbarEvidenceRunCreateRequestSchema.safeParse(
      buildBusbarEvidenceRunPayload(draft),
    );

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(issue ? describeIssue(issue) : "Review the busbar evidence inputs.");
      return;
    }

    await onSubmit(parsed.data);
  }

  // One text or decimal input of the study; the field name is the draft key.
  const studyText = (
    label: string,
    field: keyof BusbarEvidenceDraft,
    inputMode: "decimal" | "text" = "text",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={field}
        value={draft[field]}
        onChange={(event) =>
          updateStudy({ [field]: event.target.value } as Partial<BusbarEvidenceDraft>)
        }
      />
    </label>
  );

  const decimal = (label: string, field: keyof BusbarEvidenceDraft) =>
    studyText(label, field, "decimal");

  // A select with "Choose…" first and the contract's values after it.
  const choice = <T extends string>(
    field: keyof BusbarEvidenceDraft,
    label: string,
    options: readonly T[],
    labels: Record<T, string>,
  ) => (
    <label>
      {label}
      <select
        name={field}
        value={draft[field]}
        onChange={(event) =>
          updateStudy({ [field]: event.target.value } as Partial<BusbarEvidenceDraft>)
        }
      >
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );

  const busCode = draft.busCode.trim();

  return (
    <form aria-label="Busbar evidence inputs" onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice
          onKeep={keepForThisRevision}
          onClear={clearForm}
          disabled={disabled}
        />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>
        {studyText("Study code", "code")}
        {studyText("Study name", "name")}
        <SldElementPicker
          label="Bus code"
          name="busCode"
          value={draft.busCode}
          onChange={chooseBus}
          categories={["bus"]}
          elements={sldElements}
        />
        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">
              Australia / New Zealand (AS/NZS 3000) - reference data pending
            </option>
          </select>
        </label>
        <p data-profile-note="true">{PROFILE_NOTE}</p>
        {studyText("Study notes", "notes")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Fault level</legend>
        <label>
          Fault level source
          <select
            name="faultLevelSource"
            value={draft.faultLevelSource}
            onChange={(event) =>
              updateStudy({ faultLevelSource: event.target.value as FaultLevelSource })
            }
          >
            {FAULT_LEVEL_SOURCES.map((source) => (
              <option key={source} value={source}>
                {FAULT_LEVEL_SOURCE_LABELS[source]}
              </option>
            ))}
          </select>
        </label>

        {linkedSource ? (
          <FaultRunSelector
            revisionId={revisionId}
            faultRunId={draft.faultRunId}
            onChange={setFaultRunId}
            busCode={busCode}
            withPeak
            noPeakNote={NO_PEAK_NOTE}
          />
        ) : (
          <>
            {decimal("Prospective current Ik'' (kA)", "typedProspectiveCurrentKa")}
            {decimal("Peak current ip (kA)", "typedPeakCurrentKa")}
            {decimal("System nominal voltage (V)", "typedSystemNominalVoltageV")}
            {studyText("Fault bus code", "typedFaultBusCode")}
            <p data-typed-hint="true">{TYPED_HINT}</p>
          </>
        )}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Busbar</legend>
        {choice("kind", "Busbar kind", busbarKindSchema.options, BUSBAR_KIND_LABELS)}
        {choice(
          "material",
          "Conductor material",
          busbarMaterialSchema.options,
          BUSBAR_MATERIAL_LABELS,
        )}
        {decimal("Design current (A)", "designCurrentA")}
        {decimal("Rated current at site conditions (A)", "declaredRatedCurrentA")}
        <p data-rated-current-hint="true">{RATED_CURRENT_HINT}</p>
        {decimal("Builder reference ambient (°C)", "referenceAmbientC")}
        {decimal("Site ambient (°C)", "siteAmbientC")}
        <p data-site-ambient-hint="true">{SITE_AMBIENT_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Short-circuit withstand</legend>
        {decimal("Rated short-time withstand current Icw (kA)", "shortTimeWithstandCurrentKa")}
        {decimal("Icw duration (s)", "shortTimeWithstandDurationS")}
        {decimal("Required fault duration (s)", "requiredFaultDurationS")}
        <p data-fault-duration-hint="true">{FAULT_DURATION_HINT}</p>
        {decimal("Rated peak withstand current Ipk (kA)", "peakWithstandCurrentKa")}
        {decimal("Rated operational voltage Ue (V)", "ratedOperationalVoltageV")}
        <p data-withstand-hint="true">{WITHSTAND_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Neutral bar</legend>
        {decimal("Design neutral current (A)", "designNeutralCurrentA")}
        {decimal("Neutral bar rated current (A)", "declaredNeutralRatingA")}
        {/* A yes/no answer is a select with "Not stated" first: a checkbox cannot say
            "not answered". */}
        <label>
          Harmonic data available
          <select
            name="harmonicDataAvailable"
            value={draft.harmonicDataAvailable}
            onChange={(event) =>
              updateStudy({
                harmonicDataAvailable: event.target.value as BusbarEvidenceDraft["harmonicDataAvailable"],
              })
            }
          >
            <option value="">Not stated</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <p data-neutral-hint="true">{NEUTRAL_HINT}</p>
      </fieldset>

      {draft.kind === "BUSBAR_TRUNKING" ? (
        <fieldset disabled={disabled}>
          <legend>Busbar trunking</legend>
          {decimal("Route length (m)", "routeLengthM")}
          {decimal("Declared voltage drop (mV/A/m)", "declaredVoltageDropMvPerAM")}
          {decimal("Allowable voltage drop (%)", "allowableVoltageDropPercent")}
          {choice(
            "voltageDropBasis",
            "Voltage drop basis",
            voltageDropBasisSchema.options,
            VOLTAGE_DROP_BASIS_LABELS,
          )}
          <p data-trunking-hint="true">{TRUNKING_HINT}</p>
        </fieldset>
      ) : null}

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Check busbar
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}

export default BusbarEvidenceForm;
